import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Modal,
  Form,
  Tree,
  Popconfirm,
  message,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FolderOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import useStore from '../store/useStore';
import { categoriesAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import FormField from '../components/forms/FormField';
import RightDrawer from '../components/common/RightDrawer';
import DetailView, { DetailSection, DetailField } from '../components/common/DetailView';

const { Search } = Input;


const Categories = () => {
  const { categories, setCategories } = useStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [currentView, setCurrentView] = useState('list'); // 'list' | 'detail'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [form] = Form.useForm();

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchText.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleAddCategory = () => {
    setEditingCategory(null);
    form.resetFields();
    setDrawerVisible(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    form.setFieldsValue(category);
    setDrawerVisible(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await categoriesAPI.delete(categoryId);
      await fetchCategories();
      message.success('Category deleted successfully');
    } catch (err) {
      message.error('Failed to delete category');
    }
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setEditingCategory(null);
    form.resetFields();
  };

  const handleDrawerSave = async () => {
    try {
      setDrawerLoading(true);
      const values = await form.validateFields();

      if (editingCategory) {
        await categoriesAPI.update(editingCategory.id, values);
        message.success('Category updated successfully');
      } else {
        await categoriesAPI.create(values);
        message.success('Category created successfully');
      }

      await fetchCategories();
      handleDrawerClose();
    } catch (err) {
      message.error('Failed to save category');
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleViewCategory = (category) => {
    setSelectedCategory(category);
    setCurrentView('detail');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedCategory(null);
  };

  const buildTreeData = (categories, parentId = null) => {
    return categories
      .filter(cat => cat.parentId === parentId)
      .map(category => ({
        title: (
          <div className="flex items-center justify-between group">
            <div className="flex items-center gap-2">
              <FolderOutlined />
              <span>{category.name}</span>
              <Tag size="small">{category.productCount || 0} products</Tag>
            </div>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              <Space size="small">
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEditCategory(category)}
                />
                <Popconfirm
                  title="Delete Category"
                  description="Are you sure you want to delete this category?"
                  onConfirm={() => handleDeleteCategory(category.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    danger
                  />
                </Popconfirm>
              </Space>
            </div>
          </div>
        ),
        key: category.id,
        children: buildTreeData(categories, category.id),
      }));
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div className="flex items-center gap-2">
          <FolderOutlined />
          <span className="font-medium">{name}</span>
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (description) => description || '-',
    },
    {
      title: 'Parent Category',
      dataIndex: 'parentName',
      key: 'parentName',
      render: (parentName) => parentName || 'Root Category',
    },
    {
      title: 'Products',
      dataIndex: 'productCount',
      key: 'productCount',
      render: (count) => (
        <Tag color="blue">{count || 0} products</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditCategory(record)}
          />
          <Popconfirm
            title="Delete Category"
            description="Are you sure you want to delete this category?"
            onConfirm={() => handleDeleteCategory(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              icon={<DeleteOutlined />}
              danger
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  if (loading) {
    return <LoadingSpinner size="large" tip="Loading categories..." />;
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error}
        onRetry={fetchCategories}
        title="Failed to load categories"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Categories</h1>
          <p className="text-gray-600">Organize your products into categories</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddCategory}
          size="large"
        >
          Add Category
        </Button>
      </div>

      {/* Search */}
      {currentView === 'list' && (
      <Card>
        <Search
          placeholder="Search categories..."
          allowClear
          onSearch={handleSearch}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ maxWidth: 400 }}
          enterButton={<SearchOutlined />}
        />
      </Card>
      )}

      {currentView === 'list' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tree View */}
          <Card title="Category Hierarchy" className="h-fit">
            <Tree
              treeData={buildTreeData(filteredCategories)}
              defaultExpandAll
              showLine
              showIcon={false}
              className="category-tree"
            />
          </Card>

          {/* Table View */}
          <Card title="Category List">
            <Table
              dataSource={filteredCategories}
              columns={columns}
              rowKey="id"
              size="small"
              onRow={(record) => ({
                onClick: () => handleViewCategory(record),
                className: 'cursor-pointer hover:bg-gray-50'
              })}
              pagination={{
                pageSize: 10,
                showSizeChanger: false,
              }}
            />
          </Card>
        </div>
      ) : (
        <Card title="Category Details">
          {selectedCategory && (
            <DetailView
              title={selectedCategory.name}
              subtitle={selectedCategory.parentName || 'Root Category'}
              onBack={handleBackToList}
              onEdit={() => handleEditCategory(selectedCategory)}
              onDelete={() => {
                Modal.confirm({
                  title: 'Delete Category',
                  content: 'Are you sure you want to delete this category?',
                  onOk: () => handleDeleteCategory(selectedCategory.id),
                });
              }}
            >
              <DetailSection title="Category Information">
                <DetailField label="Category Name" value={selectedCategory.name} />
                <DetailField label="Description" value={selectedCategory.description || '-'} />
                <DetailField label="Parent Category" value={selectedCategory.parentName || 'Root Category'} />
                <DetailField label="Products" value={`${selectedCategory.productCount || 0} products`} />
                <DetailField label="Status" value={selectedCategory.active ? 'Active' : 'Inactive'} />
              </DetailSection>
            </DetailView>
          )}
        </Card>
      )}

      {/* Right Drawer for Add/Edit */}
      <RightDrawer
        title={editingCategory ? 'Edit Category' : 'Add New Category'}
        open={drawerVisible}
        onClose={handleDrawerClose}
        loading={drawerLoading}
        width={560}
      >
        <Form form={form} layout="vertical" className="mt-2" onFinish={handleDrawerSave} id="drawer-form">
          <FormField
            name="name"
            label="Category Name"
            type="input"
            required
            placeholder="Enter category name"
          />

          <FormField
            name="description"
            label="Description"
            type="textarea"
            placeholder="Enter category description"
            rows={3}
          />

          <FormField
            name="parentId"
            label="Parent Category"
            type="select"
            options={[
              { value: null, label: 'Root Category' },
              ...categories
                .filter(cat => !editingCategory || cat.id !== editingCategory.id)
                .map(cat => ({ value: cat.id, label: cat.name }))
            ]}
            placeholder="Select parent category"
          />

          <FormField
            name="active"
            label="Status"
            type="switch"
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
        </Form>
      </RightDrawer>
    </div>
  );
};

export default Categories;
